import { useState } from "react";
import { useLanguage } from "../../../i18n/LanguageContext";

export default function SequenceExercise({
  exercise,
  submittingLearningExercise,
  submitLearningExercise,
  styles,
}) {
  const { language } = useLanguage();

  // =========================================================
  // Canonical data
  // These remain the source of truth for ordering/validation.
  // =========================================================
  const correctItems =
    exercise.exercise_data?.items || [];

  const initialItems =
    exercise.exercise_data?.shuffled_items ||
    [...correctItems].sort(() => Math.random() - 0.5);

  const canonicalInstruction =
    exercise.exercise_data?.instruction || "";

  // =========================================================
  // Display-only ES / RU translations
  // =========================================================
  const translatedData =
    exercise.translated_exercise_data || {};

  const translatedPrompt =
    language === "es" || language === "ru"
      ? exercise.translated_prompt || null
      : null;

  const translatedInstruction =
    language === "es" || language === "ru"
      ? translatedData.instruction || null
      : null;

  const translatedItems =
    language === "es" || language === "ru"
      ? translatedData.items || []
      : [];

  // =========================================================
  // Separate legacy mixed English + Chinese display text
  // =========================================================
  const splitEnglishChinese = (text = "") => {
    const firstChineseIndex = String(text).search(
      /[\u3400-\u4DBF\u4E00-\u9FFF]/
    );

    if (firstChineseIndex === -1) {
      return {
        english: String(text).trim(),
        chinese: null,
      };
    }

    return {
      english: String(text)
        .slice(0, firstChineseIndex)
        .trim(),

      chinese: String(text)
        .slice(firstChineseIndex)
        .trim(),
    };
  };

  const promptParts =
    splitEnglishChinese(exercise.prompt || "");

  const instructionParts =
    splitEnglishChinese(canonicalInstruction);

  const englishPrompt = promptParts.english;
  const chinesePrompt = promptParts.chinese;

  const englishInstruction =
    instructionParts.english;

  const chineseInstruction =
    instructionParts.chinese;

  // =========================================================
  // Translation lookup
  // Canonical English item remains the lookup key.
  // =========================================================
  const getTranslatedItem = (canonicalItem) => {
    if (language !== "es" && language !== "ru") {
      return null;
    }

    const translatedItem = translatedItems.find(
      (item) => item.canonical === canonicalItem
    );

    return translatedItem?.text || null;
  };

  // =========================================================
  // State
  // orderedItems always contains canonical English values.
  // =========================================================
  const [orderedItems, setOrderedItems] =
    useState(initialItems);

  const [submitted, setSubmitted] =
    useState(false);

  const [isCorrect, setIsCorrect] =
    useState(false);

  const moveItem = (index, direction) => {
    if (submitted) return;

    const newItems = [...orderedItems];

    const targetIndex =
      direction === "up"
        ? index - 1
        : index + 1;

    if (
      targetIndex < 0 ||
      targetIndex >= newItems.length
    ) {
      return;
    }

    [newItems[index], newItems[targetIndex]] = [
      newItems[targetIndex],
      newItems[index],
    ];

    setOrderedItems(newItems);
  };

  // =========================================================
  // Submit
  // Validation remains 100% canonical English.
  // =========================================================
  const handleSubmit = async () => {
    const correct =
      orderedItems.length === correctItems.length &&
      orderedItems.every(
        (item, index) =>
          item === correctItems[index]
      );

    const success =
      await submitLearningExercise({
        exerciseId: exercise.id,
        answer: {
          ordered_items: orderedItems,
        },
        isCorrect: correct,
      });

    if (!success) return;

    setIsCorrect(correct);
    setSubmitted(true);
  };

  const handleTryAgain = () => {
    setOrderedItems(
      [...correctItems].sort(
        () => Math.random() - 0.5
      )
    );

    setSubmitted(false);
    setIsCorrect(false);
  };

  return (
    <div
      style={{
        ...styles.emptyBox,
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        Put in Order
      </div>

      {/* =====================================================
          Prompt
          EN: English only
          ZH: English + Chinese
          ES/RU: English + selected translation
      ====================================================== */}

      {englishPrompt && (
        <div style={styles.quizPath}>
          {englishPrompt}
        </div>
      )}

      {language === "zh" && chinesePrompt && (
        <div
          style={{
            ...styles.quizPath,
            marginTop: "4px",
            color: "#6b7280",
          }}
        >
          {chinesePrompt}
        </div>
      )}

      {(language === "es" ||
        language === "ru") &&
        translatedPrompt && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {translatedPrompt}
          </div>
        )}

      {/* =====================================================
          Instruction
      ====================================================== */}

      {englishInstruction &&
        englishInstruction !== englishPrompt && (
          <div
            style={{
              marginTop: "8px",
              lineHeight: "1.5",
            }}
          >
            {englishInstruction}
          </div>
        )}

      {language === "zh" &&
        chineseInstruction && (
          <div
            style={{
              marginTop: "4px",
              color: "#6b7280",
              lineHeight: "1.5",
            }}
          >
            {chineseInstruction}
          </div>
        )}

      {(language === "es" ||
        language === "ru") &&
        translatedInstruction &&
        translatedInstruction !==
          translatedPrompt && (
          <div
            style={{
              marginTop: "4px",
              color: "#6b7280",
              lineHeight: "1.5",
            }}
          >
            {translatedInstruction}
          </div>
        )}

      {/* =====================================================
          Sequence items
      ====================================================== */}

      <div
        style={{
          marginTop: "16px",
          display: "grid",
          gap: "10px",
        }}
      >
        {orderedItems.map((item, index) => {
          const itemParts =
            splitEnglishChinese(item);

          const englishItem =
            itemParts.english;

          const chineseItem =
            itemParts.chinese;

          const translatedItem =
            getTranslatedItem(item);

          return (
            <div
              key={`${exercise.id}-${index}`}
              style={{
                padding: "12px",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  minWidth: "28px",
                }}
              >
                {index + 1}.
              </div>

              <div style={{ flex: 1 }}>
                {/* Canonical English */}
                <div>{englishItem}</div>

                {/* Chinese */}
                {language === "zh" &&
                  chineseItem && (
                    <div
                      style={{
                        marginTop: "3px",
                        color: "#6b7280",
                        lineHeight: "1.5",
                      }}
                    >
                      {chineseItem}
                    </div>
                  )}

                {/* Spanish / Russian */}
                {(language === "es" ||
                  language === "ru") &&
                  translatedItem && (
                    <div
                      style={{
                        marginTop: "3px",
                        color: "#6b7280",
                        lineHeight: "1.5",
                      }}
                    >
                      {translatedItem}
                    </div>
                  )}
              </div>

              {!submitted && (
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                  }}
                >
                  <button
                    style={
                      styles.smallSecondaryButton
                    }
                    disabled={index === 0}
                    onClick={() =>
                      moveItem(index, "up")
                    }
                  >
                    ↑
                  </button>

                  <button
                    style={
                      styles.smallSecondaryButton
                    }
                    disabled={
                      index ===
                      orderedItems.length - 1
                    }
                    onClick={() =>
                      moveItem(index, "down")
                    }
                  >
                    ↓
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* =====================================================
          Submit / Result
      ====================================================== */}

      {!submitted ? (
        <button
          style={{
            ...styles.smallPrimaryButton,
            marginTop: "16px",
          }}
          disabled={submittingLearningExercise}
          onClick={handleSubmit}
        >
          {submittingLearningExercise
            ? "Submitting..."
            : "Submit Order"}
        </button>
      ) : (
        <div style={{ marginTop: "16px" }}>
          {isCorrect ? (
            <div
              style={{
                color: "#16a34a",
                fontWeight: "bold",
              }}
            >
              Correct
            </div>
          ) : (
            <div style={{ color: "#dc2626" }}>
              <div
                style={{
                  fontWeight: "bold",
                }}
              >
                Incorrect
              </div>

              <div
                style={{
                  marginTop: "8px",
                }}
              >
                Correct order:
              </div>

              <ol>
                {correctItems.map(
                  (item, index) => {
                    const itemParts =
                      splitEnglishChinese(item);

                    const englishItem =
                      itemParts.english;

                    const chineseItem =
                      itemParts.chinese;

                    const translatedItem =
                      getTranslatedItem(item);

                    return (
                      <li key={index}>
                        <div>
                          {englishItem}
                        </div>

                        {language === "zh" &&
                          chineseItem && (
                            <div
                              style={{
                                color: "#6b7280",
                                marginTop: "2px",
                              }}
                            >
                              {chineseItem}
                            </div>
                          )}

                        {(language === "es" ||
                          language === "ru") &&
                          translatedItem && (
                            <div
                              style={{
                                color: "#6b7280",
                                marginTop: "2px",
                              }}
                            >
                              {translatedItem}
                            </div>
                          )}
                      </li>
                    );
                  }
                )}
              </ol>
            </div>
          )}

          <button
            style={{
              ...styles.smallSecondaryButton,
              marginTop: "10px",
            }}
            onClick={handleTryAgain}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}